import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Spinner from './Spinner';
import axios from 'axios';
import './COwnerCliente.css';

type Cliente = {
  _id?: string;
  nome: string;
  descricao?: string;
  createdBy?: { fullName?: string; username?: string } | string;
  modifiedBy?: { fullName?: string; username?: string } | string;
  createdAt?: string;
  updatedAt?: string;
};

type User = {
  _id: string;
  username: string;
  fullName?: string;
  email: string;
};

type UserRelation = {
  _id: string;
  userId: User;
  clienteId: string;
  createdBy?: { name?: string };
  createdAt?: string;
  isAdmin?: boolean;
};

type Questionario = {
  _id: string;
  titulo: string;
  descricao?: string;
  ativo: boolean;
  versao: number;
  perguntas: any[];
  pontuacaoTotal: number;
  pontuacaoAprovacao?: number;
  createdBy?: { name?: string };
  createdAt?: string;
};

type Politica = {
  _id: string;
  nome: string;
  descricao?: string;
  objetivos?: string;
  controles: string[];
  ativo: boolean;
  createdBy?: { username?: string; fullName?: string };
  createdAt?: string;
  updatedAt?: string;
};

type SapControl = {
  id: string;
  name: string;
  description: string;
  category: string;
  severity: string;
  frameworks: string[];
  risk?: string;
};

const COwnerCliente: React.FC = () => {
  const { clienteId } = useParams<{ clienteId: string }>();
  const navigate = useNavigate();
  const [theme] = useState(localStorage.getItem('phoenixTheme') || 'light');
  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para usuários
  const [users, setUsers] = useState<UserRelation[]>([]);
  const [updatingAdminIds, setUpdatingAdminIds] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [searchUserQuery, setSearchUserQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;
  
  // Estados para questionários
  const [questionarios, setQuestionarios] = useState<Questionario[]>([]);
  const [loadingQuestionarios, setLoadingQuestionarios] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'tipos' | 'sistemas' | 'questionarios' | 'politicas'>('users');
  
  // Estados para tipos de solicitação
  const [showAddTipoModal, setShowAddTipoModal] = useState(false);
  const [newTipo, setNewTipo] = useState('');
  
  // Estados para sistemas
  const [showAddSistemaModal, setShowAddSistemaModal] = useState(false);
  const [showEditSistemaModal, setShowEditSistemaModal] = useState(false);
  const [editingSistemaIndex, setEditingSistemaIndex] = useState<number | null>(null);
  const [newSistema, setNewSistema] = useState({ nome: '', descricao: '', integrationId: '' });
  const [availableIntegrations, setAvailableIntegrations] = useState<any[]>([]);
  const [integrationsMap, setIntegrationsMap] = useState<Record<string, any>>({});
  
  // Estados para modal de confirmação de remoção
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{ id: string; name: string } | null>(null);
  
  // Estados para modal de confirmação de exclusão de questionário
  const [showDeleteQuestionarioModal, setShowDeleteQuestionarioModal] = useState(false);
  const [questionarioToDelete, setQuestionarioToDelete] = useState<{ id: string; titulo: string } | null>(null);
  
  // Estados para modal de criar questionário
  const [showCreateQuestionarioModal, setShowCreateQuestionarioModal] = useState(false);
  const [newQuestionario, setNewQuestionario] = useState({
    titulo: '',
    descricao: '',
    ativo: true,
    pontuacaoAprovacao: 1
  });
  
  // Estados para modal de editar questionário
  const [showEditQuestionarioModal, setShowEditQuestionarioModal] = useState(false);
  const [editingQuestionario, setEditingQuestionario] = useState<Questionario | null>(null);
  const [perguntas, setPerguntas] = useState<any[]>([]);
  const [expandedPerguntas, setExpandedPerguntas] = useState<Set<string>>(new Set());
  
  // Estados para modal de importar questionário
  const [showImportQuestionarioModal, setShowImportQuestionarioModal] = useState(false);
  const [allQuestionarios, setAllQuestionarios] = useState<any[]>([]);
  const [searchImportQuery, setSearchImportQuery] = useState('');
  const [selectedQuestionarioToImport, setSelectedQuestionarioToImport] = useState<any | null>(null);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [importData, setImportData] = useState({
    titulo: '',
    descricao: '',
    tiposSolicitacao: [] as string[]
  });
  
  // Estados para políticas
  const [politicas, setPoliticas] = useState<Politica[]>([]);
  const [loadingPoliticas, setLoadingPoliticas] = useState(false);
  const [sapControls, setSapControls] = useState<SapControl[]>([]);
  const [showCreatePoliticaModal, setShowCreatePoliticaModal] = useState(false);
  const [showEditPoliticaModal, setShowEditPoliticaModal] = useState(false);
  const [showDeletePoliticaModal, setShowDeletePoliticaModal] = useState(false);
  const [editingPolitica, setEditingPolitica] = useState<Politica | null>(null);
  const [politicaToDelete, setPoliticaToDelete] = useState<{ id: string; nome: string } | null>(null);
  const [newPolitica, setNewPolitica] = useState({
    nome: '',
    descricao: '',
    objetivos: '',
    controles: [] as string[],
    ativo: true
  });

  // Estados para filtros de controles
  const [controleSearchQuery, setControleSearchQuery] = useState('');
  const [controleFilterCategory, setControleFilterCategory] = useState('');
  const [controleFilterSeverity, setControleFilterSeverity] = useState('');
  const [controleFilterFramework, setControleFilterFramework] = useState('');

  // Funções de tradução para exibição
  const translateCategory = (category: string): string => {
    const translations: Record<string, string> = {
      'authorization': 'Autorização',
      'access': 'Acesso',
      'segregation': 'Segregação de Funções',
      'audit': 'Auditoria',
      'security': 'Segurança',
      'compliance': 'Compliance'
    };
    return translations[category] || category;
  };

  const translateSeverity = (severity: string): string => {
    const translations: Record<string, string> = {
      'critical': 'Crítico',
      'high': 'Alto',
      'medium': 'Médio',
      'low': 'Baixo'
    };
    return translations[severity] || severity;
  };

  // Estado para notificações toast
  const [toast, setToast] = useState<{ visible: boolean; message: string; variant?: 'info' | 'danger' | 'success' }>({ 
    visible: false, 
    message: '', 
    variant: 'info' 
  });

  const showToast = (message: string, variant: 'info' | 'danger' | 'success' = 'info') => {
    setToast({ visible: true, message, variant });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 4000);
  };

  // Função para filtrar controles
  const getFilteredControles = () => {
    return sapControls.filter(controle => {
      const matchesSearch = !controleSearchQuery || 
        controle.id.toLowerCase().includes(controleSearchQuery.toLowerCase()) ||
        controle.name.toLowerCase().includes(controleSearchQuery.toLowerCase()) ||
        controle.description.toLowerCase().includes(controleSearchQuery.toLowerCase());
      
      const matchesCategory = !controleFilterCategory || controle.category === controleFilterCategory;
      const matchesSeverity = !controleFilterSeverity || controle.severity === controleFilterSeverity;
      const matchesFramework = !controleFilterFramework || 
        (controle.frameworks && controle.frameworks.includes(controleFilterFramework));
      
      return matchesSearch && matchesCategory && matchesSeverity && matchesFramework;
    });
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
  }, [theme]);

  useEffect(() => {
    loadCliente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  useEffect(() => {
    if (cliente?._id) {
      loadUsers();
      loadQuestionarios();
      loadPoliticas();
      loadSapControls();
      loadAvailableIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente?._id]);

  const loadCliente = async () => {
    if (!clienteId) {
      setError('ID do cliente não informado');
      setLoading(false);
      return;
    }

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash ? `${baseNoSlash}/api/cowner-clientes/${clienteId}` : `/api/cowner-clientes/${clienteId}`;

    setLoading(true);
    try {
      const res = await axios.get(url, { withCredentials: true });
      setCliente(res.data);
      setError(null);
    } catch (err: any) {
      console.error('Erro ao carregar cliente:', err);
      setError(err?.response?.data?.message || err.message || 'Erro ao carregar cliente');
      setCliente(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!cliente?._id) return;
    
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-userxcliente/cliente/${cliente._id}` 
      : `/api/cowner-userxcliente/cliente/${cliente._id}`;

    setLoadingUsers(true);
    try {
      const res = await axios.get(url, { withCredentials: true });
      setUsers(res.data);
    } catch (err: any) {
      console.error('Erro ao carregar usuários:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadAvailableUsers = async () => {
    if (!cliente?._id) return;
    
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-userxcliente/cliente/${cliente._id}/available` 
      : `/api/cowner-userxcliente/cliente/${cliente._id}/available`;

    try {
      const res = await axios.get(url, { withCredentials: true });
      setAvailableUsers(res.data);
    } catch (err: any) {
      console.error('Erro ao carregar usuários disponíveis:', err);
    }
  };

  const loadQuestionarios = async () => {
    if (!cliente?._id) return;
    
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-questionarios/cliente/${cliente._id}` 
      : `/api/cowner-questionarios/cliente/${cliente._id}`;

    setLoadingQuestionarios(true);
    try {
      const res = await axios.get(url, { withCredentials: true });
      setQuestionarios(res.data);
    } catch (err: any) {
      console.error('Erro ao carregar questionários:', err);
    } finally {
      setLoadingQuestionarios(false);
    }
  };

  const loadPoliticas = async () => {
    if (!cliente?._id) return;
    
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner/cliente/${cliente._id}/politicas` 
      : `/api/cowner/cliente/${cliente._id}/politicas`;

    setLoadingPoliticas(true);
    try {
      const res = await axios.get(url, { withCredentials: true });
      setPoliticas(res.data.politicas || []);
    } catch (err: any) {
      console.error('Erro ao carregar políticas:', err);
      setPoliticas([]);
    } finally {
      setLoadingPoliticas(false);
    }
  };

  const loadSapControls = async () => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/sap-controls` 
      : `/api/sap-controls`;

    try {
      const res = await axios.get(url, { withCredentials: true });
      setSapControls(res.data.controls || []);
    } catch (err: any) {
      console.error('Erro ao carregar controles SAP:', err);
      setSapControls([]);
    }
  };

  const loadAvailableIntegrations = async () => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-clientes/available-integrations` 
      : `/api/cowner-clientes/available-integrations`;

    try {
      const res = await axios.get(url, { withCredentials: true });
      const integrations = res.data || [];
      setAvailableIntegrations(integrations);
      
      // Criar mapa de integrações para lookup rápido
      const map: Record<string, any> = {};
      integrations.forEach((integration: any) => {
        map[integration._id] = integration;
      });
      setIntegrationsMap(map);
    } catch (err: any) {
      console.error('Erro ao carregar integrações:', err);
      setAvailableIntegrations([]);
      setIntegrationsMap({});
    }
  };

  const handleAddUser = async () => {
    if (!selectedUserId || !cliente?._id) return;
    
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-userxcliente/cliente/${cliente._id}` 
      : `/api/cowner-userxcliente/cliente/${cliente._id}`;

    try {
      await axios.post(url, { userId: selectedUserId }, { withCredentials: true });
      setShowAddUserModal(false);
      setSelectedUserId('');
      loadUsers();
    } catch (err: any) {
      console.error('Erro ao adicionar usuário:', err);
      alert(err?.response?.data?.message || 'Erro ao adicionar usuário');
    }
  };

  const openRemoveModal = (relationId: string, userName: string) => {
    setUserToRemove({ id: relationId, name: userName });
    setShowRemoveModal(true);
  };

  const handleRemoveUser = async () => {
    if (!userToRemove) return;
    
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-userxcliente/${userToRemove.id}` 
      : `/api/cowner-userxcliente/${userToRemove.id}`;

    try {
      await axios.delete(url, { withCredentials: true });
      setShowRemoveModal(false);
      setUserToRemove(null);
      loadUsers();
    } catch (err: any) {
      console.error('Erro ao remover usuário:', err);
      alert('Erro ao remover usuário');
    }
  };

  const openAddUserModal = () => {
    loadAvailableUsers();
    setSearchUserQuery('');
    setCurrentPage(1);
    setShowAddUserModal(true);
  };

  const openCreateQuestionarioModal = () => {
    setNewQuestionario({ titulo: '', descricao: '', ativo: true, pontuacaoAprovacao: 1 });
    setShowCreateQuestionarioModal(true);
  };

  const openAddSistemaModal = () => {
    setNewSistema({ nome: '', descricao: '', integrationId: '' });
    loadAvailableIntegrations();
    setShowAddSistemaModal(true);
  };

  const openEditSistemaModal = (sistema: any, index: number) => {
    setNewSistema({
      nome: sistema.nome || '',
      descricao: sistema.descricao || '',
      integrationId: sistema.integrationId || ''
    });
    setEditingSistemaIndex(index);
    loadAvailableIntegrations();
    setShowEditSistemaModal(true);
  };

  const closeEditSistemaModal = () => {
    setShowEditSistemaModal(false);
    setEditingSistemaIndex(null);
    setNewSistema({ nome: '', descricao: '', integrationId: '' });
  };

  const handleCreateQuestionario = async () => {
    if (!newQuestionario.titulo.trim()) {
      alert('O título do questionário é obrigatório');
      return;
    }

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-questionarios/cliente/${cliente?._id}` 
      : `/api/cowner-questionarios/cliente/${cliente?._id}`;

    try {
      const payload = {
        ...newQuestionario,
        perguntas: [],
        versao: 1,
        pontuacaoTotal: 0
      };

      await axios.post(url, payload, { withCredentials: true });
      setShowCreateQuestionarioModal(false);
      setNewQuestionario({ titulo: '', descricao: '', ativo: true, pontuacaoAprovacao: 1 });
      loadQuestionarios();
    } catch (err: any) {
      console.error('Erro ao criar questionário:', err);
      alert(err?.response?.data?.message || 'Erro ao criar questionário');
    }
  };

  const handleEditQuestionario = async (questionarioId: string) => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-questionarios/${questionarioId}` 
      : `/api/cowner-questionarios/${questionarioId}`;

    try {
      const res = await axios.get(url, { withCredentials: true });
      setEditingQuestionario(res.data);
      setPerguntas(res.data.perguntas || []);
      // Iniciar com todas as perguntas colapsadas
      setExpandedPerguntas(new Set());
      setShowEditQuestionarioModal(true);
    } catch (err: any) {
      console.error('Erro ao carregar questionário:', err);
      alert('Erro ao carregar questionário');
    }
  };

  const togglePergunta = (perguntaId: string) => {
    setExpandedPerguntas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(perguntaId)) {
        newSet.delete(perguntaId);
      } else {
        newSet.add(perguntaId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedPerguntas(new Set(perguntas.map(p => p.id)));
  };

  const collapseAll = () => {
    setExpandedPerguntas(new Set());
  };

  const handleAddTipo = async () => {
    if (!newTipo.trim()) {
      alert('Digite um tipo de solicitação');
      return;
    }

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-clientes/${cliente?._id}` 
      : `/api/cowner-clientes/${cliente?._id}`;

    try {
      const tiposAtuais = (cliente as any).tiposSolicitacao || [];
      await axios.put(url, { 
        ...cliente,
        tiposSolicitacao: [...tiposAtuais, newTipo.trim()]
      }, { withCredentials: true });
      
      setShowAddTipoModal(false);
      setNewTipo('');
      loadCliente();
    } catch (err: any) {
      console.error('Erro ao adicionar tipo:', err);
      alert(err?.response?.data?.message || 'Erro ao adicionar tipo');
    }
  };

  const handleRemoveTipo = async (tipo: string) => {
    if (!window.confirm(`Deseja remover o tipo "${tipo}"?`)) return;

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-clientes/${cliente?._id}` 
      : `/api/cowner-clientes/${cliente?._id}`;

    try {
      const tiposAtuais = (cliente as any).tiposSolicitacao || [];
      await axios.put(url, { 
        ...cliente,
        tiposSolicitacao: tiposAtuais.filter((t: string) => t !== tipo)
      }, { withCredentials: true });
      
      loadCliente();
    } catch (err: any) {
      console.error('Erro ao remover tipo:', err);
      alert('Erro ao remover tipo');
    }
  };

  const handleAddSistema = async () => {
    if (!newSistema.nome.trim()) {
      alert('Digite o nome do sistema');
      return;
    }

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-clientes/${cliente?._id}` 
      : `/api/cowner-clientes/${cliente?._id}`;

    try {
      const sistemasAtuais = (cliente as any).sistemas || [];
      const novoSistema: any = { 
        nome: newSistema.nome.trim(), 
        descricao: newSistema.descricao.trim() 
      };
      
      // Adiciona integrationId se foi selecionado
      if (newSistema.integrationId) {
        novoSistema.integrationId = newSistema.integrationId;
      }
      
      await axios.put(url, { 
        ...cliente,
        sistemas: [...sistemasAtuais, novoSistema]
      }, { withCredentials: true });
      
      setShowAddSistemaModal(false);
      setNewSistema({ nome: '', descricao: '', integrationId: '' });
      loadCliente();
    } catch (err: any) {
      console.error('Erro ao adicionar sistema:', err);
      alert(err?.response?.data?.message || 'Erro ao adicionar sistema');
    }
  };

  const handleEditSistema = async () => {
    if (!newSistema.nome.trim()) {
      alert('Digite o nome do sistema');
      return;
    }

    if (editingSistemaIndex === null) return;

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-clientes/${cliente?._id}` 
      : `/api/cowner-clientes/${cliente?._id}`;

    try {
      const sistemasAtuais = (cliente as any).sistemas || [];
      const sistemasAtualizados = [...sistemasAtuais];
      
      const sistemaAtualizado: any = { 
        nome: newSistema.nome.trim(), 
        descricao: newSistema.descricao.trim() 
      };
      
      // Adiciona integrationId se foi selecionado
      if (newSistema.integrationId) {
        sistemaAtualizado.integrationId = newSistema.integrationId;
      }
      
      sistemasAtualizados[editingSistemaIndex] = sistemaAtualizado;
      
      await axios.put(url, { 
        ...cliente,
        sistemas: sistemasAtualizados
      }, { withCredentials: true });
      
      closeEditSistemaModal();
      loadCliente();
    } catch (err: any) {
      console.error('Erro ao editar sistema:', err);
      alert(err?.response?.data?.message || 'Erro ao editar sistema');
    }
  };

  const handleRemoveSistema = async (nome: string) => {
    if (!window.confirm(`Deseja remover o sistema "${nome}"?`)) return;

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-clientes/${cliente?._id}` 
      : `/api/cowner-clientes/${cliente?._id}`;

    try {
      const sistemasAtuais = (cliente as any).sistemas || [];
      await axios.put(url, { 
        ...cliente,
        sistemas: sistemasAtuais.filter((s: any) => s.nome !== nome)
      }, { withCredentials: true });
      
      loadCliente();
    } catch (err: any) {
      console.error('Erro ao remover sistema:', err);
      alert('Erro ao remover sistema');
    }
  };

  // Funções auxiliares para modais de política
  const resetControleFilters = () => {
    setControleSearchQuery('');
    setControleFilterCategory('');
    setControleFilterSeverity('');
    setControleFilterFramework('');
  };

  const closeCreatePoliticaModal = () => {
    setShowCreatePoliticaModal(false);
    resetControleFilters();
  };

  const closeEditPoliticaModal = () => {
    setShowEditPoliticaModal(false);
    setEditingPolitica(null);
    resetControleFilters();
  };

  // Handlers para Políticas
  const handleCreatePolitica = async () => {
    if (!cliente) {
      showToast('Cliente não carregado', 'danger');
      return;
    }
    
    if (!newPolitica.nome.trim()) {
      showToast('Digite o nome da política', 'danger');
      return;
    }

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = `${baseNoSlash}/api/cowner/cliente/${cliente._id}/politicas`;

    try {
      await axios.post(url, {
        nome: newPolitica.nome.trim(),
        descricao: newPolitica.descricao.trim(),
        objetivos: newPolitica.objetivos.trim(),
        controles: newPolitica.controles,
        ativo: newPolitica.ativo
      }, { withCredentials: true });
      
      closeCreatePoliticaModal();
      setNewPolitica({ nome: '', descricao: '', objetivos: '', controles: [], ativo: true });
      loadPoliticas();
      showToast('Política criada com sucesso!', 'success');
    } catch (err: any) {
      console.error('Erro ao criar política:', err);
      showToast(err?.response?.data?.message || 'Erro ao criar política', 'danger');
    }
  };

  const handleEditPolitica = async () => {
    if (!newPolitica.nome.trim() || !editingPolitica) {
      showToast('Digite o nome da política', 'danger');
      return;
    }

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = `${baseNoSlash}/api/cowner/politicas/${editingPolitica._id}`;

    try {
      await axios.put(url, {
        nome: newPolitica.nome.trim(),
        descricao: newPolitica.descricao.trim(),
        objetivos: newPolitica.objetivos.trim(),
        controles: newPolitica.controles,
        ativo: newPolitica.ativo
      }, { withCredentials: true });
      
      closeEditPoliticaModal();
      setNewPolitica({ nome: '', descricao: '', objetivos: '', controles: [], ativo: true });
      loadPoliticas();
      showToast('Política atualizada com sucesso!', 'success');
    } catch (err: any) {
      console.error('Erro ao editar política:', err);
      showToast(err?.response?.data?.message || 'Erro ao editar política', 'danger');
    }
  };

  const handleDeletePolitica = async () => {
    if (!politicaToDelete) return;

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = `${baseNoSlash}/api/cowner/politicas/${politicaToDelete.id}`;

    try {
      await axios.delete(url, { withCredentials: true });
      
      setShowDeletePoliticaModal(false);
      setPoliticaToDelete(null);
      loadPoliticas();
      showToast('Política excluída com sucesso!', 'success');
    } catch (err: any) {
      console.error('Erro ao excluir política:', err);
      showToast(err?.response?.data?.message || 'Erro ao excluir política', 'danger');
    }
  };

  const openDeleteQuestionarioModal = (questionarioId: string, titulo: string) => {
    setQuestionarioToDelete({ id: questionarioId, titulo });
    setShowDeleteQuestionarioModal(true);
  };

  const confirmDeleteQuestionario = async () => {
    if (!questionarioToDelete) return;

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-questionarios/${questionarioToDelete.id}` 
      : `/api/cowner-questionarios/${questionarioToDelete.id}`;

    try {
      await axios.delete(url, { withCredentials: true });
      setShowDeleteQuestionarioModal(false);
      setQuestionarioToDelete(null);
      loadQuestionarios();
    } catch (err: any) {
      console.error('Erro ao excluir questionário:', err);
      alert(err?.response?.data?.message || 'Erro ao excluir questionário');
    }
  };

  const loadAllQuestionarios = async () => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-questionarios` 
      : `/api/cowner-questionarios`;

    try {
      const response = await axios.get(url, { withCredentials: true });
      setAllQuestionarios(response.data);
    } catch (err: any) {
      console.error('Erro ao carregar questionários:', err);
      alert(err?.response?.data?.message || 'Erro ao carregar questionários');
    }
  };

  const openImportQuestionarioModal = () => {
    setShowImportQuestionarioModal(true);
    loadAllQuestionarios();
  };

  const handleSelectQuestionarioToImport = (questionario: any) => {
    setSelectedQuestionarioToImport(questionario);
    setImportData({
      titulo: questionario.titulo,
      descricao: questionario.descricao || '',
      tiposSolicitacao: []
    });
    setShowImportQuestionarioModal(false);
    setShowImportConfirmModal(true);
  };

  const confirmImportQuestionario = async () => {
    if (!selectedQuestionarioToImport || !importData.titulo.trim()) {
      alert('Título é obrigatório');
      return;
    }

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-questionarios/cliente/${cliente?._id}` 
      : `/api/cowner-questionarios/cliente/${cliente?._id}`;

    try {
      const payload = {
        titulo: importData.titulo.trim(),
        descricao: importData.descricao,
        ativo: true,
        perguntas: selectedQuestionarioToImport.perguntas || [],
        tiposSolicitacao: importData.tiposSolicitacao,
        pontuacaoTotal: selectedQuestionarioToImport.pontuacaoTotal || 0
      };

      await axios.post(url, payload, { withCredentials: true });
      setShowImportConfirmModal(false);
      setSelectedQuestionarioToImport(null);
      setImportData({ titulo: '', descricao: '', tiposSolicitacao: [] });
      setSearchImportQuery('');
      loadQuestionarios();
    } catch (err: any) {
      console.error('Erro ao importar questionário:', err);
      alert(err?.response?.data?.message || 'Erro ao importar questionário');
    }
  };

  const handleAddPergunta = () => {
    const novaPergunta = {
      id: `pergunta-${Date.now()}`,
      ordem: perguntas.length + 1,
      titulo: '',
      descricao: '',
      informacaoAjuda: '',
      tipo: 'unica-escolha',
      obrigatoria: true,
      peso: 1,
      pontuacaoMaxima: 100,
      alternativas: [],
    };
    setPerguntas([...perguntas, novaPergunta]);
  };

  const handleRemovePergunta = (perguntaId: string) => {
    setPerguntas(perguntas.filter(p => p.id !== perguntaId));
  };

  const handleUpdatePergunta = (perguntaId: string, field: string, value: any) => {
    setPerguntas(perguntas.map(p => 
      p.id === perguntaId ? { ...p, [field]: value } : p
    ));
  };

  const handleAddAlternativa = (perguntaId: string) => {
    setPerguntas(perguntas.map(p => {
      if (p.id === perguntaId) {
        const novaAlternativa = {
          id: `alt-${Date.now()}`,
          texto: '',
          valor: 0,
          proximaPergunta: '',
          recomendacao: '',
          rejeitarAutomaticamente: false,
        };
        return { ...p, alternativas: [...(p.alternativas || []), novaAlternativa] };
      }
      return p;
    }));
  };

  const handleRemoveAlternativa = (perguntaId: string, alternativaId: string) => {
    setPerguntas(perguntas.map(p => {
      if (p.id === perguntaId) {
        return { ...p, alternativas: p.alternativas.filter((a: any) => a.id !== alternativaId) };
      }
      return p;
    }));
  };

  const handleUpdateAlternativa = (perguntaId: string, alternativaId: string, field: string, value: any) => {
    setPerguntas(perguntas.map(p => {
      if (p.id === perguntaId) {
        return {
          ...p,
          alternativas: p.alternativas.map((a: any) => 
            a.id === alternativaId ? { ...a, [field]: value } : a
          )
        };
      }
      return p;
    }));
  };

  const handleSaveQuestionario = async () => {
    if (!editingQuestionario) return;

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-questionarios/${editingQuestionario._id}` 
      : `/api/cowner-questionarios/${editingQuestionario._id}`;

    try {
      // Recalcular pontuação total
      const pontuacaoTotal = perguntas.reduce((total, p) => total + (p.pontuacaoMaxima || 0), 0);

      // Validar pontuacaoAprovacao
      const pontuacaoAprovacao = editingQuestionario.pontuacaoAprovacao || 1;
      const pontuacaoAprovacaoFinal = pontuacaoTotal > 0 
        ? Math.min(Math.max(1, pontuacaoAprovacao), pontuacaoTotal)
        : pontuacaoAprovacao;

      const payload: any = {
        titulo: editingQuestionario.titulo,
        descricao: editingQuestionario.descricao,
        ativo: editingQuestionario.ativo,
        perguntas: perguntas.map((p, index) => ({ ...p, ordem: index + 1 })),
        pontuacaoTotal,
        pontuacaoAprovacao: pontuacaoAprovacaoFinal,
      };

      // Add tiposSolicitacao if present
      if ((editingQuestionario as any).tiposSolicitacao) {
        payload.tiposSolicitacao = (editingQuestionario as any).tiposSolicitacao;
      }

      await axios.put(url, payload, { withCredentials: true });
      setShowEditQuestionarioModal(false);
      setEditingQuestionario(null);
      setPerguntas([]);
      loadQuestionarios();
    } catch (err: any) {
      console.error('Erro ao salvar questionário:', err);
      alert(err?.response?.data?.message || 'Erro ao salvar questionário');
    }
  };

  // Filtrar usuários disponíveis com base na busca
  const filteredAvailableUsers = availableUsers.filter(user => {
    const searchLower = searchUserQuery.toLowerCase();
    const fullName = (user.fullName || '').toLowerCase();
    const username = (user.username || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return fullName.includes(searchLower) || username.includes(searchLower) || email.includes(searchLower);
  });

  // Paginação
  const totalPages = Math.ceil(filteredAvailableUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = filteredAvailableUsers.slice(startIndex, endIndex);

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserName = (user?: { fullName?: string; username?: string } | string) => {
    if (!user) return '-';
    if (typeof user === 'string') return user;
    return user.fullName || user.username || '-';
  };

  const handleBack = () => {
    navigate('/cowner-admin-clientes');
  };

  if (loading) {
    return (
      <>
        <Spinner />
      </>
    );
  }

  if (error || !cliente) {
    return (
      <>
        <div style={{ marginTop: 80, paddingLeft: 24, paddingRight: 24, paddingTop: 32, paddingBottom: 32 }}>
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Erro ao carregar cliente</h4>
            <p>{error || 'Cliente não encontrado'}</p>
            <hr />
            <button className="btn btn-secondary" onClick={handleBack}>
              Voltar para lista de clientes
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ 
        marginTop: 80, 
        margin: 0,
        paddingLeft: 16, 
        paddingRight: 16, 
        paddingTop: 32, 
        paddingBottom: 16, 
        maxWidth: '100vw', 
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Breadcrumb / Navigation */}
        <div className="cowner-breadcrumb-container mb-4">
          <button className="btn btn-link text-decoration-none p-0 me-2" onClick={handleBack} aria-label="Voltar">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
            </svg>
          </button>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <button 
                  className="btn btn-link p-0 text-decoration-none" 
                  onClick={handleBack}
                  style={{ border: 'none', background: 'none' }}
                >
                  Administrar Clientes
                </button>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {cliente.nome}
              </li>
            </ol>
          </nav>
        </div>

        {/* Informações do Cliente - Topo */}
        <div className="card mb-3" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', marginTop: '1rem' }}>
          <div className="card-body" style={{ padding: '1.5rem' }}>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div style={{ flex: 1 }}>
                <h3 className="mb-1">{cliente.nome}</h3>
                {cliente.descricao && (
                  <p className="text-muted mb-0">{cliente.descricao}</p>
                )}
              </div>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => navigate('/cowner-admin-clientes')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                </svg>
                Voltar
              </button>
            </div>
            
            <div className="cliente-info-grid">
              <div className="cliente-info-item">
                <label className="form-label fw-bold text-muted small mb-1">ID TÉCNICO</label>
                <p className="mb-0">
                  <code style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{cliente._id}</code>
                </p>
              </div>
              <div className="cliente-info-item">
                <label className="form-label fw-bold text-muted small mb-1">CRIADO POR</label>
                <p className="mb-0">{getUserName(cliente.createdBy)}</p>
              </div>
              <div className="cliente-info-item">
                <label className="form-label fw-bold text-muted small mb-1">CRIADO EM</label>
                <p className="mb-0">{formatDate(cliente.createdAt)}</p>
              </div>
              <div className="cliente-info-item">
                <label className="form-label fw-bold text-muted small mb-1">MODIFICADO POR</label>
                <p className="mb-0">{getUserName(cliente.modifiedBy)}</p>
              </div>
              <div className="cliente-info-item">
                <label className="form-label fw-bold text-muted small mb-1">MODIFICADO EM</label>
                <p className="mb-0">{formatDate(cliente.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs de Usuários e Questionários - Embaixo */}
        <div className="card" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                  onClick={() => setActiveTab('users')}
                  type="button"
                  role="tab"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002a.274.274 0 0 1-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM6.936 9.28a5.88 5.88 0 0 0-1.23-.247A7.35 7.35 0 0 0 5 9c-4 0-5 3-5 4 0 .667.333 1 1 1h4.216A2.238 2.238 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816zM4.92 10A5.493 5.493 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275zM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0zm3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                  </svg>
                  Usuários ({users.length})
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'tipos' ? 'active' : ''}`}
                  onClick={() => setActiveTab('tipos')}
                  type="button"
                  role="tab"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z"/>
                  </svg>
                  Tipos de Solicitação ({(cliente as any)?.tiposSolicitacao?.length || 0})
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'sistemas' ? 'active' : ''}`}
                  onClick={() => setActiveTab('sistemas')}
                  type="button"
                  role="tab"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 1.5A1.5 1.5 0 0 1 1.5 0h13A1.5 1.5 0 0 1 16 1.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5v-13zM1.5 1a.5.5 0 0 0-.5.5V5h4V1H1.5zM5 6H1v4h4V6zm1 4h4V6H6v4zm-1 1H1v3.5a.5.5 0 0 0 .5.5H5v-4zm1 0v4h4v-4H6zm5 0v4h3.5a.5.5 0 0 0 .5-.5V11h-4zm0-1h4V6h-4v4zm0-5h4V1.5a.5.5 0 0 0-.5-.5H11v4zm-1 0V1H6v4h4z"/>
                  </svg>
                  Sistemas ({(cliente as any)?.sistemas?.length || 0})
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'questionarios' ? 'active' : ''}`}
                  onClick={() => setActiveTab('questionarios')}
                  type="button"
                  role="tab"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.5 1A1.5 1.5 0 0 0 1 2.5v11A1.5 1.5 0 0 0 2.5 15h6.086a1.5 1.5 0 0 0 1.06-.44l4.915-4.914A1.5 1.5 0 0 0 15 8.586V2.5A1.5 1.5 0 0 0 13.5 1h-11zM2 2.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 .5.5V8H9.5A1.5 1.5 0 0 0 8 9.5V14H2.5a.5.5 0 0 1-.5-.5v-11zm7 11.293V9.5a.5.5 0 0 1 .5-.5h4.293L9 13.793z"/>
                  </svg>
                  Questionários ({questionarios.length})
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'politicas' ? 'active' : ''}`}
                  onClick={() => setActiveTab('politicas')}
                  type="button"
                  role="tab"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM2.04 4.326c.325 1.329 2.532 2.54 3.717 3.19.48.263.793.434.743.484-.08.08-.162.158-.242.234-.416.396-.787.749-.758 1.266.035.634.618.824 1.214 1.017.577.188 1.168.38 1.286.983.082.417-.075.988-.22 1.52-.215.782-.406 1.48.22 1.48 1.5-.5 3.798-3.186 4-5 .138-1.243-2-2-3.5-2.5-.478-.16-.755.081-.99.284-.172.15-.322.279-.51.216-.445-.148-2.5-2-1.5-2.5.78-.39.952-.171 1.227.182.078.099.163.208.273.318.609.304.662-.132.723-.633.039-.322.081-.671.277-.867.434-.434 1.265-.791 2.028-1.12.712-.306 1.365-.587 1.579-.88A7 7 0 1 0 2.04 4.327z"/>
                  </svg>
                  Políticas ({politicas.length})
                </button>
              </li>
            </ul>
          </div>
          <div className="card-body">
            {/* Tab: Usuários */}
            {activeTab === 'users' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Usuários Relacionados</h5>
                  <button className="btn btn-outline-primary" onClick={openAddUserModal}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 110-2h6V1a1 1 0 011-1z"/>
                    </svg>
                    Adicionar Usuário
                  </button>
                </div>
                
                {loadingUsers ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-5">
                    <svg width="48" height="48" viewBox="0 0 16 16" fill="currentColor" className="text-muted mb-3" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002a.274.274 0 0 1-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/>
                    </svg>
                    <p className="text-muted mb-2">Nenhum usuário relacionado</p>
                    <button className="btn btn-outline-primary" onClick={openAddUserModal}>
                      Adicionar primeiro usuário
                    </button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Administrador</th>
                          <th>Nome do Usuário</th>
                          <th>Email</th>
                          <th>Relacionado em</th>
                          <th className="text-end">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((rel) => (
                          <tr key={rel._id}>
                            <td style={{ width: 140 }}>
                              <div className="form-check form-switch d-flex justify-content-center">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id={`admin-switch-${rel._id}`}
                                  checked={!!rel.isAdmin}
                                  disabled={updatingAdminIds.includes(rel._id)}
                                  onChange={async (e) => {
                                    const next = e.target.checked;
                                    // mark as updating
                                    setUpdatingAdminIds(prev => [...prev, rel._id]);
                                    try {
                                      const base = process.env.REACT_APP_API_URL || '';
                                      const baseNoSlash = base ? base.replace(/\/$/, '') : '';
                                      const url = baseNoSlash ? `${baseNoSlash}/api/cowner-userxcliente/${rel._id}` : `/api/cowner-userxcliente/${rel._id}`;
                                      console.log('[COwnerCliente] PATCH', url, { isAdmin: next });
                                      const response = await axios.patch(url, { isAdmin: next }, { withCredentials: true });
                                      console.log('[COwnerCliente] PATCH response', response.data);
                                      const updatedRel = response.data;
                                      // Atualizar estado local com o objeto retornado (preserva populated userId)
                                      setUsers(prev => prev.map(p => p._id === rel._id ? ({
                                        _id: updatedRel._id,
                                        userId: updatedRel.userId || p.userId,
                                        clienteId: updatedRel.clienteId || p.clienteId,
                                        createdBy: updatedRel.createdBy || p.createdBy,
                                        createdAt: updatedRel.createdAt || p.createdAt,
                                        isAdmin: !!updatedRel.isAdmin
                                      } as UserRelation) : p));
                                    } catch (err: any) {
                                      console.error('Erro ao atualizar administrador:', err);
                                      alert(err?.response?.data?.message || 'Erro ao atualizar administrador');
                                    } finally {
                                      // unmark
                                      setUpdatingAdminIds(prev => prev.filter(id => id !== rel._id));
                                    }
                                  }}
                                />
                                <label className="form-check-label ms-2" htmlFor={`admin-switch-${rel._id}`}></label>
                              </div>
                            </td>
                            <td>{rel.userId?.fullName || rel.userId?.username || '-'}</td>
                            <td>{rel.userId?.email || '-'}</td>
                            <td>{formatDate(rel.createdAt)}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => openRemoveModal(rel._id, rel.userId?.fullName || rel.userId?.username || 'Usuário')}
                                title="Remover usuário"
                              >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                  <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Tipos de Solicitação */}
            {activeTab === 'tipos' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Tipos de Solicitação</h5>
                  <button className="btn btn-outline-primary" onClick={() => setShowAddTipoModal(true)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 110-2h6V1a1 1 0 011-1z"/>
                    </svg>
                    Adicionar Tipo
                  </button>
                </div>

                {!(cliente as any)?.tiposSolicitacao || (cliente as any).tiposSolicitacao.length === 0 ? (
                  <div className="text-center py-5">
                    <svg width="48" height="48" viewBox="0 0 16 16" fill="currentColor" className="text-muted mb-3" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zm8 0A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm-8 8A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm8 0A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3z"/>
                    </svg>
                    <p className="text-muted mb-2">Nenhum tipo de solicitação cadastrado</p>
                    <button className="btn btn-outline-primary" onClick={() => setShowAddTipoModal(true)}>
                      Adicionar primeiro tipo
                    </button>
                  </div>
                ) : (
                  <div className="row g-3">
                    {(cliente as any).tiposSolicitacao.map((tipo: string, index: number) => (
                      <div key={index} className="col-md-4">
                        <div className="card h-100">
                          <div className="card-body d-flex justify-content-between align-items-center">
                            <span>{tipo}</span>
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveTipo(tipo)}
                              title="Remover tipo"
                            >
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Sistemas */}
            {activeTab === 'sistemas' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Sistemas</h5>
                  <button className="btn btn-outline-primary" onClick={openAddSistemaModal}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 110-2h6V1a1 1 0 011-1z"/>
                    </svg>
                    Adicionar Sistema
                  </button>
                </div>

                {!(cliente as any)?.sistemas || (cliente as any).sistemas.length === 0 ? (
                  <div className="text-center py-5">
                    <svg width="48" height="48" viewBox="0 0 16 16" fill="currentColor" className="text-muted mb-3" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 2a2 2 0 0 0-2 2v8.01A2 2 0 0 0 2 14h5.5a.5.5 0 0 0 0-1H2a1 1 0 0 1-.966-.741l5.64-3.471L8 9.583l7-4.2V8.5a.5.5 0 0 0 1 0V4a2 2 0 0 0-2-2H2Zm3.708 6.208L1 11.105V5.383l4.708 2.825ZM1 4.217V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v.217l-7 4.2-7-4.2Z"/>
                    </svg>
                    <p className="text-muted mb-2">Nenhum sistema cadastrado</p>
                    <button className="btn btn-outline-primary" onClick={openAddSistemaModal}>
                      Adicionar primeiro sistema
                    </button>
                  </div>
                ) : (
                  <div className="row g-3">
                    {(cliente as any).sistemas.map((sistema: any, index: number) => {
                      const integration = sistema.integrationId ? integrationsMap[sistema.integrationId] : null;
                      return (
                        <div key={index} className="col-md-6">
                          <div className="card h-100">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1">
                                  <h6 className="mb-1">{sistema.nome}</h6>
                                  {integration && (
                                    <div className="d-flex align-items-center gap-1 mb-1">
                                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" className="text-primary" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12.166 8.94c-.524 1.062-1.234 2.12-1.96 3.07A31.493 31.493 0 0 1 8 14.58a31.481 31.481 0 0 1-2.206-2.57c-.726-.95-1.436-2.008-1.96-3.07C3.304 7.867 3 6.862 3 6a5 5 0 0 1 10 0c0 .862-.305 1.867-.834 2.94zM8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10z"/>
                                        <path d="M8 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 1a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                                      </svg>
                                      <small className="text-primary">
                                        <strong>{integration.name}</strong> ({integration.connectionType.toUpperCase()})
                                      </small>
                                    </div>
                                  )}
                                </div>
                                <div className="d-flex gap-1">
                                  <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => openEditSistemaModal(sistema, index)}
                                    title="Editar sistema"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
                                    </svg>
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleRemoveSistema(sistema.nome)}
                                    title="Remover sistema"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              {sistema.descricao && (
                                <p className="text-muted small mb-0">{sistema.descricao}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Questionários */}
            {activeTab === 'questionarios' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Questionários</h5>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary" onClick={openImportQuestionarioModal}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                      </svg>
                      Importar Questionário
                    </button>
                    <button className="btn btn-outline-primary" onClick={openCreateQuestionarioModal}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 110-2h6V1a1 1 0 011-1z"/>
                      </svg>
                      Criar Questionário
                    </button>
                  </div>
                </div>

                {loadingQuestionarios ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                ) : questionarios.length === 0 ? (
                  <div className="text-center py-5">
                    <svg width="48" height="48" viewBox="0 0 16 16" fill="currentColor" className="text-muted mb-3" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 1A1.5 1.5 0 0 0 1 2.5v11A1.5 1.5 0 0 0 2.5 15h6.086a1.5 1.5 0 0 0 1.06-.44l4.915-4.914A1.5 1.5 0 0 0 15 8.586V2.5A1.5 1.5 0 0 0 13.5 1h-11z"/>
                    </svg>
                    <p className="text-muted mb-2">Nenhum questionário cadastrado</p>
                    <button className="btn btn-outline-primary" onClick={openCreateQuestionarioModal}>
                      Criar primeiro questionário
                    </button>
                  </div>
                ) : (
                  <div className="row g-3">
                    {questionarios.map((q) => (
                      <div key={q._id} className="col-md-6">
                        <div className="card h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="card-title mb-0">{q.titulo}</h6>
                              <span className={`badge ${q.ativo ? 'bg-success' : 'bg-secondary'}`}>
                                {q.ativo ? 'Ativo' : 'Inativo'}
                              </span>
                            </div>
                            {q.descricao && <p className="card-text text-muted small">{q.descricao}</p>}
                            {(q as any).tiposSolicitacao && (q as any).tiposSolicitacao.length > 0 && (
                              <div className="mb-2">
                                {(q as any).tiposSolicitacao.map((tipo: string, idx: number) => (
                                  <span key={idx} className="badge bg-info me-1" style={{ fontSize: '0.7rem' }}>
                                    {tipo}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="mt-3">
                              <small className="text-muted d-block">Total de perguntas: {q.perguntas?.length || 0}</small>
                              <small className="text-muted d-block">Pontuação máxima: {q.pontuacaoTotal} pontos</small>
                              <small className="text-muted d-block">Aprovação: {q.pontuacaoAprovacao || 1} pontos</small>
                            </div>
                          </div>
                          <div className="card-footer bg-transparent">
                            <button 
                              className="btn btn-outline-primary me-2" 
                              onClick={() => handleEditQuestionario(q._id)}
                              title="Editar questionário"
                            >
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" className="me-1" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
                              </svg>
                              Editar
                            </button>
                            <button 
                              className="btn btn-outline-danger" 
                              onClick={() => openDeleteQuestionarioModal(q._id, q.titulo)}
                              title="Excluir questionário"
                            >
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" className="me-1" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                              </svg>
                              Excluir
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Políticas */}
            {activeTab === 'politicas' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Políticas de Compliance</h5>
                  <button className="btn btn-outline-primary" onClick={() => {
                    setNewPolitica({ nome: '', descricao: '', objetivos: '', controles: [], ativo: true });
                    setShowCreatePoliticaModal(true);
                  }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 110-2h6V1a1 1 0 011-1z"/>
                    </svg>
                    Criar Política
                  </button>
                </div>

                {loadingPoliticas ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                ) : politicas.length === 0 ? (
                  <div className="text-center py-5">
                    <svg width="48" height="48" viewBox="0 0 16 16" fill="currentColor" className="text-muted mb-3" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0z"/>
                    </svg>
                    <p className="text-muted mb-2">Nenhuma política cadastrada</p>
                    <button className="btn btn-outline-primary" onClick={() => {
                      setNewPolitica({ nome: '', descricao: '', objetivos: '', controles: [], ativo: true });
                      setShowCreatePoliticaModal(true);
                    }}>
                      Criar primeira política
                    </button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: '25%' }}>Nome</th>
                          <th style={{ width: '30%' }}>Descrição</th>
                          <th style={{ width: '15%' }}>Controles</th>
                          <th style={{ width: '10%' }}>Status</th>
                          <th style={{ width: '15%' }}>Criado por</th>
                          <th style={{ width: '5%' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {politicas.map((p) => (
                          <tr key={p._id}>
                            <td>
                              <strong>{p.nome}</strong>
                              {p.objetivos && (
                                <div className="small text-muted mt-1">{p.objetivos}</div>
                              )}
                            </td>
                            <td>
                              <span className="text-muted small">{p.descricao || '-'}</span>
                            </td>
                            <td>
                              <span className="badge bg-primary">
                                {p.controles?.length || 0}
                              </span>
                              {p.controles && p.controles.length > 0 && (
                                <div className="mt-1">
                                  {p.controles.slice(0, 3).map((controleId) => (
                                    <span 
                                      key={controleId} 
                                      className="badge bg-light text-dark border me-1" 
                                      style={{ fontSize: '0.65rem' }}
                                      title={sapControls.find(c => c.id === controleId)?.name || controleId}
                                    >
                                      {controleId}
                                    </span>
                                  ))}
                                  {p.controles.length > 3 && (
                                    <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem' }}>
                                      +{p.controles.length - 3}
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${p.ativo ? 'bg-success' : 'bg-secondary'}`}>
                                {p.ativo ? 'Ativa' : 'Inativa'}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {p.createdBy?.fullName || p.createdBy?.username || 'N/A'}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <button 
                                  className="btn btn-sm btn-outline-primary" 
                                  onClick={() => {
                                    setEditingPolitica(p);
                                    setNewPolitica({
                                      nome: p.nome,
                                      descricao: p.descricao || '',
                                      objetivos: p.objetivos || '',
                                      controles: p.controles || [],
                                      ativo: p.ativo
                                    });
                                    setShowEditPoliticaModal(true);
                                  }}
                                  title="Editar política"
                                >
                                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
                                  </svg>
                                </button>
                                <button 
                                  className="btn btn-sm btn-outline-danger" 
                                  onClick={() => {
                                    setPoliticaToDelete({ id: p._id, nome: p.nome });
                                    setShowDeletePoliticaModal(true);
                                  }}
                                  title="Excluir política"
                                >
                                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal: Adicionar Usuário */}
        {showAddUserModal && (
          <div className="cowner-modal-overlay" onClick={() => setShowAddUserModal(false)}>
            <div className="cowner-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
              <div className="cowner-modal-header">
                <h3>Adicionar Usuário ao Cliente</h3>
                <button className="cowner-modal-close" onClick={() => setShowAddUserModal(false)} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                {/* Barra de pesquisa */}
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Pesquisar por nome ou email..."
                    value={searchUserQuery}
                    onChange={(e) => {
                      setSearchUserQuery(e.target.value);
                      setCurrentPage(1); // Reset para primeira página ao buscar
                    }}
                  />
                </div>

                {availableUsers.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">
                      Todos os usuários já estão relacionados a este cliente.
                    </p>
                  </div>
                ) : filteredAvailableUsers.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">
                      Nenhum usuário encontrado com "{searchUserQuery}"
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Tabela de usuários */}
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="table table-hover table-sm">
                        <thead style={{ position: 'sticky', top: 0, background: 'var(--bs-body-bg)', zIndex: 1 }}>
                          <tr>
                            <th style={{ width: '50px' }}></th>
                            <th>Nome</th>
                            <th>Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.map((u) => (
                            <tr 
                              key={u._id}
                              onClick={() => handleSelectUser(u._id)}
                              style={{ cursor: 'pointer' }}
                              className={selectedUserId === u._id ? 'table-active' : ''}
                            >
                              <td className="text-center">
                                <input
                                  type="radio"
                                  name="selectedUser"
                                  checked={selectedUserId === u._id}
                                  onChange={() => handleSelectUser(u._id)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </td>
                              <td>{u.fullName || u.username}</td>
                              <td>{u.email}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Paginação */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                          Exibindo {startIndex + 1}-{Math.min(endIndex, filteredAvailableUsers.length)} de {filteredAvailableUsers.length} usuários
                        </small>
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                Anterior
                              </button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                              <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                              >
                                Próxima
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAddUserModal(false)}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handleAddUser}
                  disabled={!selectedUserId}
                >
                  Adicionar Usuário
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmação de remoção */}
        {showRemoveModal && userToRemove && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmação</h5>
                </div>
                <div className="modal-body">
                  <p>Deseja realmente remover o usuário <strong>{userToRemove.name}</strong> deste cliente?</p>
                  <div><small>Cliente: <strong>{cliente?.nome}</strong></small></div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary" 
                    onClick={() => { setShowRemoveModal(false); setUserToRemove(null); }}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger" 
                    onClick={handleRemoveUser}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Adicionar Tipo de Solicitação */}
        {showAddTipoModal && (
          <div className="cowner-modal-overlay" onClick={() => setShowAddTipoModal(false)}>
            <div className="cowner-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="cowner-modal-header">
                <h3>Adicionar Tipo de Solicitação</h3>
                <button className="cowner-modal-close" onClick={() => setShowAddTipoModal(false)} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="mb-3">
                  <label htmlFor="newTipo" className="form-label">
                    Nome do Tipo <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="newTipo"
                    value={newTipo}
                    onChange={(e) => setNewTipo(e.target.value)}
                    placeholder="Ex: Acesso SAP, Acesso rede, Aprovação de compra..."
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddTipo();
                    }}
                  />
                  <small className="text-muted">Este tipo ficará disponível para associar aos questionários</small>
                </div>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAddTipoModal(false)}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handleAddTipo}
                  disabled={!newTipo.trim()}
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Adicionar Sistema */}
        {showAddSistemaModal && (
          <div className="cowner-modal-overlay" onClick={() => setShowAddSistemaModal(false)}>
            <div className="cowner-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div className="cowner-modal-header">
                <h3>Adicionar Sistema</h3>
                <button className="cowner-modal-close" onClick={() => setShowAddSistemaModal(false)} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="mb-3">
                  <label htmlFor="nomeSistema" className="form-label">
                    Nome do Sistema <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="nomeSistema"
                    value={newSistema.nome}
                    onChange={(e) => setNewSistema({ ...newSistema, nome: e.target.value })}
                    placeholder="Ex: SAP ECC, SAP S/4HANA, Salesforce..."
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="descricaoSistema" className="form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    id="descricaoSistema"
                    rows={2}
                    value={newSistema.descricao}
                    onChange={(e) => setNewSistema({ ...newSistema, descricao: e.target.value })}
                    placeholder="Descrição opcional do sistema..."
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="integrationSistema" className="form-label">
                    Conector (Integração)
                  </label>
                  <select
                    className="form-select"
                    id="integrationSistema"
                    value={newSistema.integrationId}
                    onChange={(e) => setNewSistema({ ...newSistema, integrationId: e.target.value })}
                  >
                    <option value="">Nenhum conector selecionado</option>
                    {availableIntegrations.map((integration) => (
                      <option key={integration._id} value={integration._id}>
                        {integration.name} - {integration.host} ({integration.connectionType.toUpperCase()})
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    Selecione o conector que será usado para executar políticas neste sistema
                  </small>
                </div>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAddSistemaModal(false)}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handleAddSistema}
                  disabled={!newSistema.nome.trim()}
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Editar Sistema */}
        {showEditSistemaModal && (
          <div className="cowner-modal-overlay" onClick={closeEditSistemaModal}>
            <div className="cowner-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div className="cowner-modal-header">
                <h3>Editar Sistema</h3>
                <button className="cowner-modal-close" onClick={closeEditSistemaModal} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="mb-3">
                  <label htmlFor="editNomeSistema" className="form-label">
                    Nome do Sistema <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="editNomeSistema"
                    value={newSistema.nome}
                    onChange={(e) => setNewSistema({ ...newSistema, nome: e.target.value })}
                    placeholder="Ex: SAP ECC, SAP S/4HANA, Salesforce..."
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="editDescricaoSistema" className="form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    id="editDescricaoSistema"
                    rows={2}
                    value={newSistema.descricao}
                    onChange={(e) => setNewSistema({ ...newSistema, descricao: e.target.value })}
                    placeholder="Descrição opcional do sistema..."
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="editIntegrationSistema" className="form-label">
                    Conector (Integração)
                  </label>
                  <select
                    className="form-select"
                    id="editIntegrationSistema"
                    value={newSistema.integrationId}
                    onChange={(e) => setNewSistema({ ...newSistema, integrationId: e.target.value })}
                  >
                    <option value="">Nenhum conector selecionado</option>
                    {availableIntegrations.map((integration) => (
                      <option key={integration._id} value={integration._id}>
                        {integration.name} - {integration.host} ({integration.connectionType.toUpperCase()})
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    Selecione o conector que será usado para executar políticas neste sistema
                  </small>
                </div>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={closeEditSistemaModal}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handleEditSistema}
                  disabled={!newSistema.nome.trim()}
                >
                  Salvar Alterações
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Confirmar Exclusão de Questionário */}
        {showDeleteQuestionarioModal && questionarioToDelete && (
          <div className="cowner-modal-overlay" onClick={() => setShowDeleteQuestionarioModal(false)}>
            <div className="cowner-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="cowner-modal-header">
                <h3>Confirmar Exclusão</h3>
                <button className="cowner-modal-close" onClick={() => setShowDeleteQuestionarioModal(false)} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="alert alert-warning d-flex align-items-start" role="alert">
                  <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor" className="me-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                  </svg>
                  <div>
                    <strong>Atenção!</strong> Esta ação não pode ser desfeita.
                  </div>
                </div>
                <p className="mb-2">
                  Tem certeza que deseja excluir o questionário:
                </p>
                <p className="mb-0">
                  <strong>"{questionarioToDelete.titulo}"</strong>
                </p>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteQuestionarioModal(false)}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={confirmDeleteQuestionario}
                >
                  Excluir Questionário
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Selecionar Questionário para Importar */}
        {showImportQuestionarioModal && (
          <div className="cowner-modal-overlay" onClick={() => setShowImportQuestionarioModal(false)}>
            <div className="cowner-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
              <div className="cowner-modal-header">
                <h3>Importar Questionário</h3>
                <button className="cowner-modal-close" onClick={() => setShowImportQuestionarioModal(false)} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Pesquisar por cliente, título ou descrição..."
                    value={searchImportQuery}
                    onChange={(e) => setSearchImportQuery(e.target.value)}
                  />
                </div>

                {allQuestionarios.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">Nenhum questionário disponível para importar</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {allQuestionarios
                      .filter(q => {
                        const searchLower = searchImportQuery.toLowerCase();
                        const clienteNome = (q.clienteId?.nome || '').toLowerCase();
                        const titulo = (q.titulo || '').toLowerCase();
                        const descricao = (q.descricao || '').toLowerCase();
                        return clienteNome.includes(searchLower) || titulo.includes(searchLower) || descricao.includes(searchLower);
                      })
                      .map((q) => (
                        <div 
                          key={q._id} 
                          className="card mb-2" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelectQuestionarioToImport(q)}
                        >
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center mb-1">
                                  <span className="badge bg-secondary me-2" style={{ fontSize: '0.7rem' }}>
                                    {q.clienteId?.nome || 'Cliente não encontrado'}
                                  </span>
                                  {q.ativo ? (
                                    <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>Ativo</span>
                                  ) : (
                                    <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>Inativo</span>
                                  )}
                                </div>
                                <h6 className="mb-1">{q.titulo}</h6>
                                {q.descricao && <p className="text-muted small mb-1">{q.descricao}</p>}
                                <small className="text-muted">
                                  {q.perguntas?.length || 0} perguntas • {q.pontuacaoTotal || 0} pontos • Versão {q.versao || 1}
                                </small>
                              </div>
                              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowImportQuestionarioModal(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Confirmar Importação */}
        {showImportConfirmModal && selectedQuestionarioToImport && (
          <div className="cowner-modal-overlay" onClick={() => setShowImportConfirmModal(false)}>
            <div className="cowner-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div className="cowner-modal-header">
                <h3>Configurar Importação</h3>
                <button className="cowner-modal-close" onClick={() => setShowImportConfirmModal(false)} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="alert alert-info mb-3">
                  <small>
                    <strong>Importando de:</strong> {selectedQuestionarioToImport.clienteId?.nome || 'Cliente não encontrado'}
                    <br />
                    <strong>Perguntas:</strong> {selectedQuestionarioToImport.perguntas?.length || 0}
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="import-titulo" className="form-label">
                    Título <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="import-titulo"
                    value={importData.titulo}
                    onChange={(e) => setImportData({ ...importData, titulo: e.target.value })}
                    placeholder="Nome do questionário"
                    autoFocus
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="import-descricao" className="form-label">Descrição</label>
                  <textarea
                    className="form-control"
                    id="import-descricao"
                    value={importData.descricao}
                    onChange={(e) => setImportData({ ...importData, descricao: e.target.value })}
                    rows={3}
                    placeholder="Descrição do questionário"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Tipos de Solicitação</label>
                  <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--bs-border-color)', borderRadius: '8px', padding: '8px' }}>
                    {(cliente as any)?.tiposSolicitacao && (cliente as any).tiposSolicitacao.length > 0 ? (
                      (cliente as any).tiposSolicitacao.map((tipo: string) => (
                        <div key={tipo} className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`import-tipo-${tipo}`}
                            checked={importData.tiposSolicitacao.includes(tipo)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setImportData({ ...importData, tiposSolicitacao: [...importData.tiposSolicitacao, tipo] });
                              } else {
                                setImportData({ ...importData, tiposSolicitacao: importData.tiposSolicitacao.filter(t => t !== tipo) });
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor={`import-tipo-${tipo}`}>
                            {tipo}
                          </label>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted small mb-0">Nenhum tipo cadastrado. Adicione tipos na aba "Tipos de Solicitação".</p>
                    )}
                  </div>
                  <small className="text-muted">Selecione para quais tipos de solicitação este questionário se aplica</small>
                </div>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowImportConfirmModal(false)}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={confirmImportQuestionario}
                  disabled={!importData.titulo.trim()}
                >
                  Confirmar Importação
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de criar questionário */}
        {showCreateQuestionarioModal && (
          <div className="cowner-modal-overlay">
            <div className="cowner-modal">
              <div className="cowner-modal-header">
                <h3>Criar Questionário</h3>
                <button className="cowner-modal-close" onClick={() => setShowCreateQuestionarioModal(false)}>
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="mb-3">
                  <label htmlFor="titulo" className="form-label">
                    Título <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="titulo"
                    value={newQuestionario.titulo}
                    onChange={(e) => setNewQuestionario({ ...newQuestionario, titulo: e.target.value })}
                    placeholder="Ex: Questionário de Compliance 2025"
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="descricao" className="form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    id="descricao"
                    rows={3}
                    value={newQuestionario.descricao}
                    onChange={(e) => setNewQuestionario({ ...newQuestionario, descricao: e.target.value })}
                    placeholder="Descrição opcional do questionário"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="pontuacaoAprovacao" className="form-label">
                    Pontuação para Aprovação <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="pontuacaoAprovacao"
                    value={newQuestionario.pontuacaoAprovacao}
                    onChange={(e) => {
                      const valor = parseInt(e.target.value) || 1;
                      setNewQuestionario({ ...newQuestionario, pontuacaoAprovacao: Math.max(1, valor) });
                    }}
                    min="1"
                    placeholder="Pontuação mínima para aprovação"
                  />
                  <small className="form-text text-muted">
                    Pontuação mínima necessária para que o questionário seja considerado aprovado (mínimo: 1)
                  </small>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="ativo"
                    checked={newQuestionario.ativo}
                    onChange={(e) => setNewQuestionario({ ...newQuestionario, ativo: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="ativo">
                    Ativo
                  </label>
                </div>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowCreateQuestionarioModal(false)}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handleCreateQuestionario}
                  disabled={!newQuestionario.titulo.trim()}
                >
                  Criar Questionário
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de editar questionário */}
        {showEditQuestionarioModal && editingQuestionario && (
          <div className="cowner-modal-overlay">
            <div className="cowner-modal" style={{ maxWidth: '1200px', width: '95%', maxHeight: '90vh' }}>
              <div className="cowner-modal-header">
                <h3>Editar Questionário</h3>
                <button className="cowner-modal-close" onClick={() => setShowEditQuestionarioModal(false)}>
                  ×
                </button>
              </div>
              <div className="cowner-modal-body" style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
                {/* Informações básicas */}
                <div className="mb-4">
                  <h5 className="mb-3">Informações Básicas</h5>
                  <div className="mb-3">
                    <label htmlFor="edit-titulo" className="form-label">
                      Título <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="edit-titulo"
                      value={editingQuestionario.titulo}
                      onChange={(e) => setEditingQuestionario({ ...editingQuestionario, titulo: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="edit-descricao" className="form-label">
                      Descrição
                    </label>
                    <textarea
                      className="form-control"
                      id="edit-descricao"
                      rows={2}
                      value={editingQuestionario.descricao || ''}
                      onChange={(e) => setEditingQuestionario({ ...editingQuestionario, descricao: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="edit-pontuacaoAprovacao" className="form-label">
                      Pontuação para Aprovação <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="edit-pontuacaoAprovacao"
                      value={editingQuestionario.pontuacaoAprovacao || 1}
                      onChange={(e) => {
                        const valor = parseInt(e.target.value) || 1;
                        const pontuacaoMax = editingQuestionario.pontuacaoTotal || 0;
                        const pontuacaoFinal = pontuacaoMax > 0 ? Math.min(Math.max(1, valor), pontuacaoMax) : Math.max(1, valor);
                        setEditingQuestionario({ ...editingQuestionario, pontuacaoAprovacao: pontuacaoFinal });
                      }}
                      min="1"
                      max={editingQuestionario.pontuacaoTotal || undefined}
                      placeholder="Pontuação mínima para aprovação"
                    />
                    <small className="form-text text-muted">
                      Pontuação mínima: 1 | Pontuação máxima do questionário: {editingQuestionario.pontuacaoTotal || 0}
                    </small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Tipos de Solicitação</label>
                    <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--bs-border-color)', borderRadius: '8px', padding: '16px' }}>
                      {(cliente as any)?.tiposSolicitacao && (cliente as any).tiposSolicitacao.length > 0 ? (
                        (cliente as any).tiposSolicitacao.map((tipo: string) => (
                          <div key={tipo} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`tipo-${tipo}`}
                              checked={(editingQuestionario as any).tiposSolicitacao?.includes(tipo) || false}
                              onChange={(e) => {
                                const tipos = (editingQuestionario as any).tiposSolicitacao || [];
                                if (e.target.checked) {
                                  setEditingQuestionario({ ...editingQuestionario, tiposSolicitacao: [...tipos, tipo] } as any);
                                } else {
                                  setEditingQuestionario({ ...editingQuestionario, tiposSolicitacao: tipos.filter((t: string) => t !== tipo) } as any);
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor={`tipo-${tipo}`}>
                              {tipo}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted small mb-0">Nenhum tipo cadastrado. Adicione tipos na aba "Tipos de Solicitação".</p>
                      )}
                    </div>
                    <small className="text-muted">Selecione para quais tipos de solicitação este questionário se aplica</small>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="edit-ativo"
                      checked={editingQuestionario.ativo}
                      onChange={(e) => setEditingQuestionario({ ...editingQuestionario, ativo: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="edit-ativo">
                      Ativo
                    </label>
                  </div>
                </div>

                {/* Perguntas */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Perguntas ({perguntas.length})</h5>
                    <button className="btn btn-sm btn-outline-primary" onClick={handleAddPergunta}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" className="me-1" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 110-2h6V1a1 1 0 011-1z"/>
                      </svg>
                      Adicionar Pergunta
                    </button>
                  </div>

                  {perguntas.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <p>Nenhuma pergunta adicionada</p>
                      <small>Clique em "Adicionar Pergunta" para começar</small>
                    </div>
                  ) : (
                    <>
                      <div className="d-flex gap-2 mb-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={expandAll}>
                          Expandir todas
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={collapseAll}>
                          Colapsar todas
                        </button>
                      </div>
                      <div className="perguntas-list" style={{ width: '100%' }}>
                        {perguntas.map((pergunta, index) => {
                          const isExpanded = expandedPerguntas.has(pergunta.id);
                          const tipoLabels: Record<string, string> = {
                            'unica-escolha': 'Única Escolha',
                            'multipla-escolha': 'Múltipla Escolha',
                            'texto-livre': 'Texto Livre',
                            'numero': 'Número',
                            'data': 'Data'
                          };
                          const tipoLabel = tipoLabels[pergunta.tipo] || pergunta.tipo;

                          return (
                            <div key={pergunta.id} className="card mb-3" style={{ width: '100%' }}>
                              <div 
                                className="card-header d-flex justify-content-between align-items-center"
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => togglePergunta(pergunta.id)}
                              >
                                <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ 
                                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s'
                                  }}>
                                    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                                  </svg>
                                  <strong>Pergunta {index + 1}</strong>
                                  <span className="text-muted small">
                                    • {tipoLabel} • {pergunta.titulo ? pergunta.titulo.substring(0, 50) + (pergunta.titulo.length > 50 ? '...' : '') : 'Sem título'}
                                  </span>
                                </div>
                                <button 
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemovePergunta(pergunta.id);
                                  }}
                                  title="Remover pergunta"
                                >
                                  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </button>
                              </div>
                              {isExpanded && (
                                <div className="card-body">
                            <div className="row mb-2">
                              <div className="col-12">
                                <label className="form-label small">Título da Pergunta</label>
                                <textarea
                                  className="form-control form-control-sm"
                                  value={pergunta.titulo}
                                  onChange={(e) => handleUpdatePergunta(pergunta.id, 'titulo', e.target.value)}
                                  placeholder="Ex: Qual é o nível de conformidade?"
                                  rows={2}
                                  style={{ resize: 'vertical', minHeight: '60px' }}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <div className="col-12">
                                <label className="form-label small">Informação de Ajuda</label>
                                <textarea
                                  className="form-control form-control-sm"
                                  value={pergunta.informacaoAjuda || ''}
                                  onChange={(e) => handleUpdatePergunta(pergunta.id, 'informacaoAjuda', e.target.value)}
                                  placeholder="Texto de auxílio para ajudar na seleção da resposta correta"
                                  rows={2}
                                  style={{ resize: 'vertical', minHeight: '60px' }}
                                />
                              </div>
                            </div>
                            
                            <div className="row mb-2">
                              <div className="col-md-3">
                                <label className="form-label small">Tipo</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={pergunta.tipo}
                                  onChange={(e) => handleUpdatePergunta(pergunta.id, 'tipo', e.target.value)}
                                >
                                  <option value="unica-escolha">Única Escolha</option>
                                  <option value="multipla-escolha">Múltipla Escolha</option>
                                  <option value="texto-livre">Texto Livre</option>
                                  <option value="numero">Número</option>
                                  <option value="data">Data</option>
                                </select>
                              </div>
                              <div className="col-md-3">
                                <label className="form-label small">Peso</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={pergunta.peso}
                                  onChange={(e) => handleUpdatePergunta(pergunta.id, 'peso', parseFloat(e.target.value))}
                                  min="0"
                                  step="0.1"
                                />
                              </div>
                              <div className="col-md-3">
                                <label className="form-label small">Pontuação Máxima</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={pergunta.pontuacaoMaxima}
                                  onChange={(e) => handleUpdatePergunta(pergunta.id, 'pontuacaoMaxima', parseInt(e.target.value))}
                                  min="0"
                                />
                              </div>
                              <div className="col-md-3">
                                <label className="form-label small">&nbsp;</label>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={pergunta.obrigatoria}
                                    onChange={(e) => handleUpdatePergunta(pergunta.id, 'obrigatoria', e.target.checked)}
                                  />
                                  <label className="form-check-label small">
                                    Obrigatória
                                  </label>
                                </div>
                              </div>
                            </div>

                            {/* Alternativas (somente para escolha única/múltipla) */}
                            {(pergunta.tipo === 'unica-escolha' || pergunta.tipo === 'multipla-escolha') && (
                              <div className="mt-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <label className="form-label small mb-0">Alternativas</label>
                                  <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleAddAlternativa(pergunta.id)}
                                  >
                                    + Adicionar
                                  </button>
                                </div>
                                {pergunta.alternativas?.map((alt: any) => (
                                  <div key={alt.id} className="mb-3 p-2" style={{ background: 'rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                                    <div className="row g-2 mb-2">
                                      <div className="col-md-6">
                                        <label className="form-label small mb-1">Texto da alternativa</label>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={alt.texto}
                                          onChange={(e) => handleUpdateAlternativa(pergunta.id, alt.id, 'texto', e.target.value)}
                                          placeholder="Ex: Sim, Não, Talvez..."
                                        />
                                      </div>
                                      <div className="col-md-2">
                                        <label className="form-label small mb-1">Valor/Pontos</label>
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          value={alt.valor}
                                          onChange={(e) => handleUpdateAlternativa(pergunta.id, alt.id, 'valor', parseFloat(e.target.value))}
                                          placeholder="0"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="form-label small mb-1">Próxima pergunta</label>
                                        <select
                                          className="form-select form-select-sm"
                                          value={alt.proximaPergunta || ''}
                                          onChange={(e) => handleUpdateAlternativa(pergunta.id, alt.id, 'proximaPergunta', e.target.value)}
                                        >
                                          <option value="">Próxima em ordem</option>
                                          {perguntas.map((p: any) => (
                                            <option key={p.id} value={p.id}>
                                              Pergunta {p.ordem}: {p.titulo.substring(0, 30)}...
                                            </option>
                                          ))}
                                          <option value="FIM">Finalizar questionário</option>
                                        </select>
                                      </div>
                                      <div className="col-md-1 d-flex align-items-end">
                                        <button 
                                          className="btn btn-sm btn-outline-danger w-100"
                                          onClick={() => handleRemoveAlternativa(pergunta.id, alt.id)}
                                          title="Remover alternativa"
                                        >
                                          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                    <div className="row g-2">
                                      <div className="col-12">
                                        <label className="form-label small mb-1">Texto de recomendação (para relatório)</label>
                                        <textarea
                                          className="form-control form-control-sm"
                                          value={alt.recomendacao || ''}
                                          onChange={(e) => handleUpdateAlternativa(pergunta.id, alt.id, 'recomendacao', e.target.value)}
                                          placeholder="Texto que será exibido no relatório caso esta alternativa seja escolhida"
                                          rows={2}
                                          style={{ resize: 'vertical', minHeight: '50px' }}
                                        />
                                      </div>
                                      <div className="col-12 mt-2">
                                        <div className="form-check" style={{ paddingLeft: '2rem' }}>
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`rejeitar-${pergunta.id}-${alt.id}`}
                                            checked={alt.rejeitarAutomaticamente || false}
                                            onChange={(e) => handleUpdateAlternativa(pergunta.id, alt.id, 'rejeitarAutomaticamente', e.target.checked)}
                                          />
                                          <label className="form-check-label small text-danger" htmlFor={`rejeitar-${pergunta.id}-${alt.id}`} style={{ cursor: 'pointer' }}>
                                            <strong>Rejeitar automaticamente</strong> - Força rejeição mesmo com pontuação suficiente
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEditQuestionarioModal(false)}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handleSaveQuestionario}
                  disabled={!editingQuestionario.titulo.trim()}
                >
                  Salvar Questionário
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Criar Política */}
        {showCreatePoliticaModal && (
          <div className="cowner-modal-overlay">
            <div className="cowner-modal" style={{ maxWidth: '1400px', width: '95%' }}>
              <div className="cowner-modal-header">
                <h3>Criar Nova Política</h3>
                <button className="cowner-modal-close" onClick={closeCreatePoliticaModal} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="mb-3">
                  <label htmlFor="nomePolitica" className="form-label">
                    Nome da Política <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="nomePolitica"
                    value={newPolitica.nome}
                    onChange={(e) => setNewPolitica({ ...newPolitica, nome: e.target.value })}
                    placeholder="Ex: Política de Segregação de Funções"
                    autoFocus
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="descricaoPolitica" className="form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    id="descricaoPolitica"
                    rows={3}
                    value={newPolitica.descricao}
                    onChange={(e) => setNewPolitica({ ...newPolitica, descricao: e.target.value })}
                    placeholder="Descreva o propósito desta política..."
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="objetivosPolitica" className="form-label">
                    Objetivos
                  </label>
                  <textarea
                    className="form-control"
                    id="objetivosPolitica"
                    rows={3}
                    value={newPolitica.objetivos}
                    onChange={(e) => setNewPolitica({ ...newPolitica, objetivos: e.target.value })}
                    placeholder="Liste os objetivos desta política..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Controles Associados</label>
                  
                  {/* Controles selecionados */}
                  {newPolitica.controles.length > 0 && (
                    <div className="mb-2 p-2" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)', borderRadius: '0.25rem' }}>
                      <small className="d-block mb-1"><strong>{newPolitica.controles.length} controle(s) selecionado(s):</strong></small>
                      <div className="d-flex flex-wrap gap-1">
                        {newPolitica.controles.map(controlId => (
                          <span key={controlId} className="badge bg-primary d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                            {controlId}
                            <button
                              type="button"
                              className="btn-close btn-close-white ms-1"
                              style={{ fontSize: '0.6rem' }}
                              onClick={() => setNewPolitica({
                                ...newPolitica,
                                controles: newPolitica.controles.filter(id => id !== controlId)
                              })}
                              aria-label="Remover"
                            />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Filtros */}
                  <div className="row g-2 mb-2">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="🔍 Pesquisar controles..."
                        value={controleSearchQuery}
                        onChange={(e) => setControleSearchQuery(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={controleFilterCategory}
                        onChange={(e) => setControleFilterCategory(e.target.value)}
                      >
                        <option value="">Todas Categorias</option>
                        <option value="authorization">Autorização</option>
                        <option value="access">Acesso</option>
                        <option value="segregation">Segregação de Funções</option>
                        <option value="audit">Auditoria</option>
                        <option value="security">Segurança</option>
                        <option value="compliance">Compliance</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={controleFilterSeverity}
                        onChange={(e) => setControleFilterSeverity(e.target.value)}
                      >
                        <option value="">Todas Severidades</option>
                        <option value="critical">Crítico</option>
                        <option value="high">Alto</option>
                        <option value="medium">Médio</option>
                        <option value="low">Baixo</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={controleFilterFramework}
                        onChange={(e) => setControleFilterFramework(e.target.value)}
                      >
                        <option value="">Todos Frameworks</option>
                        <option value="SOX">SOX</option>
                        <option value="ISO27001">ISO 27001</option>
                        <option value="COBIT">COBIT</option>
                        <option value="LGPD">LGPD</option>
                        <option value="GDPR">GDPR</option>
                        <option value="NIST">NIST</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary w-100"
                        onClick={resetControleFilters}
                        title="Limpar filtros"
                      >
                        🔄 Limpar
                      </button>
                    </div>
                  </div>

                  {/* Botão Selecionar Todos */}
                  <div className="mb-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        const filteredIds = getFilteredControles().map(c => c.id);
                        const allSelected = filteredIds.every(id => newPolitica.controles.includes(id));
                        
                        if (allSelected) {
                          // Desmarcar todos os filtrados
                          setNewPolitica({
                            ...newPolitica,
                            controles: newPolitica.controles.filter(id => !filteredIds.includes(id))
                          });
                        } else {
                          // Marcar todos os filtrados
                          const novosControles = Array.from(new Set([...newPolitica.controles, ...filteredIds]));
                          setNewPolitica({
                            ...newPolitica,
                            controles: novosControles
                          });
                        }
                      }}
                    >
                      {getFilteredControles().length > 0 && 
                       getFilteredControles().every(c => newPolitica.controles.includes(c.id))
                        ? '☑ Desmarcar Todos'
                        : '☐ Selecionar Todos'
                      } ({getFilteredControles().length})
                    </button>
                  </div>

                  {/* Lista de controles */}
                  <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--bs-border-color)', borderRadius: '0.25rem', padding: '0.75rem', backgroundColor: 'var(--bs-body-bg)' }}>
                    {sapControls.length === 0 ? (
                      <p className="text-muted small mb-0">Nenhum controle disponível</p>
                    ) : getFilteredControles().length === 0 ? (
                      <p className="text-muted small mb-0">Nenhum controle encontrado com os filtros aplicados</p>
                    ) : (
                      getFilteredControles().map((controle) => (
                        <div key={controle.id} className="form-check mb-3 p-3" style={{ backgroundColor: newPolitica.controles.includes(controle.id) ? 'rgba(13, 110, 253, 0.1)' : 'transparent', borderRadius: '0.25rem', position: 'relative', border: '1px solid var(--bs-border-color-translucent)' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`controle-${controle.id}`}
                            checked={newPolitica.controles.includes(controle.id)}
                            style={{ position: 'relative', zIndex: 1, marginTop: '0.35rem', flexShrink: 0 }}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewPolitica({
                                  ...newPolitica,
                                  controles: [...newPolitica.controles, controle.id]
                                });
                              } else {
                                setNewPolitica({
                                  ...newPolitica,
                                  controles: newPolitica.controles.filter(id => id !== controle.id)
                                });
                              }
                            }}
                          />
                          <label className="form-check-label w-100" htmlFor={`controle-${controle.id}`} style={{ cursor: 'pointer' }}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div style={{ flex: 1 }}>
                                <strong>{controle.id}</strong> - {controle.name}
                                <br />
                                <small className="text-muted">{controle.description}</small>
                              </div>
                              <div className="d-flex flex-column gap-1 ms-2" style={{ alignItems: 'flex-end' }}>
                                <div className="d-flex gap-1">
                                  <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>{translateCategory(controle.category)}</span>
                                  <span className={`badge ${controle.severity === 'critical' ? 'bg-danger' : controle.severity === 'high' ? 'bg-warning' : controle.severity === 'medium' ? 'bg-info' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                    {translateSeverity(controle.severity)}
                                  </span>
                                </div>
                                {controle.frameworks && controle.frameworks.length > 0 && (
                                  <div className="d-flex gap-1 flex-wrap justify-content-end">
                                    {controle.frameworks.map((fw: string) => (
                                      <span key={fw} className="badge bg-primary" style={{ fontSize: '0.6rem' }}>{fw}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  <small className="text-muted">Selecione os controles que farão parte desta política</small>
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="ativoPolitica"
                    checked={newPolitica.ativo}
                    onChange={(e) => setNewPolitica({ ...newPolitica, ativo: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="ativoPolitica">
                    Política ativa
                  </label>
                </div>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={closeCreatePoliticaModal}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handleCreatePolitica}
                  disabled={!newPolitica.nome.trim()}
                >
                  Criar Política
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Editar Política */}
        {showEditPoliticaModal && editingPolitica && (
          <div className="cowner-modal-overlay">
            <div className="cowner-modal" style={{ maxWidth: '1400px', width: '95%' }}>
              <div className="cowner-modal-header">
                <h3>Editar Política</h3>
                <button className="cowner-modal-close" onClick={closeEditPoliticaModal} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="mb-3">
                  <label htmlFor="editNomePolitica" className="form-label">
                    Nome da Política <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="editNomePolitica"
                    value={newPolitica.nome}
                    onChange={(e) => setNewPolitica({ ...newPolitica, nome: e.target.value })}
                    autoFocus
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="editDescricaoPolitica" className="form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    id="editDescricaoPolitica"
                    rows={3}
                    value={newPolitica.descricao}
                    onChange={(e) => setNewPolitica({ ...newPolitica, descricao: e.target.value })}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="editObjetivosPolitica" className="form-label">
                    Objetivos
                  </label>
                  <textarea
                    className="form-control"
                    id="editObjetivosPolitica"
                    rows={3}
                    value={newPolitica.objetivos}
                    onChange={(e) => setNewPolitica({ ...newPolitica, objetivos: e.target.value })}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Controles Associados</label>
                  
                  {/* Controles selecionados */}
                  {newPolitica.controles.length > 0 && (
                    <div className="mb-2 p-2" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)', borderRadius: '0.25rem' }}>
                      <small className="d-block mb-1"><strong>{newPolitica.controles.length} controle(s) selecionado(s):</strong></small>
                      <div className="d-flex flex-wrap gap-1">
                        {newPolitica.controles.map(controlId => (
                          <span key={controlId} className="badge bg-primary d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                            {controlId}
                            <button
                              type="button"
                              className="btn-close btn-close-white ms-1"
                              style={{ fontSize: '0.6rem' }}
                              onClick={() => setNewPolitica({
                                ...newPolitica,
                                controles: newPolitica.controles.filter(id => id !== controlId)
                              })}
                              aria-label="Remover"
                            />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Filtros */}
                  <div className="row g-2 mb-2">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="🔍 Pesquisar controles..."
                        value={controleSearchQuery}
                        onChange={(e) => setControleSearchQuery(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={controleFilterCategory}
                        onChange={(e) => setControleFilterCategory(e.target.value)}
                      >
                        <option value="">Todas Categorias</option>
                        <option value="authorization">Autorização</option>
                        <option value="access">Acesso</option>
                        <option value="segregation">Segregação de Funções</option>
                        <option value="audit">Auditoria</option>
                        <option value="security">Segurança</option>
                        <option value="compliance">Compliance</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={controleFilterSeverity}
                        onChange={(e) => setControleFilterSeverity(e.target.value)}
                      >
                        <option value="">Todas Severidades</option>
                        <option value="critical">Crítico</option>
                        <option value="high">Alto</option>
                        <option value="medium">Médio</option>
                        <option value="low">Baixo</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={controleFilterFramework}
                        onChange={(e) => setControleFilterFramework(e.target.value)}
                      >
                        <option value="">Todos Frameworks</option>
                        <option value="SOX">SOX</option>
                        <option value="ISO27001">ISO 27001</option>
                        <option value="COBIT">COBIT</option>
                        <option value="LGPD">LGPD</option>
                        <option value="GDPR">GDPR</option>
                        <option value="NIST">NIST</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary w-100"
                        onClick={resetControleFilters}
                        title="Limpar filtros"
                      >
                        🔄 Limpar
                      </button>
                    </div>
                  </div>

                  {/* Botão Selecionar Todos */}
                  <div className="mb-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        const filteredIds = getFilteredControles().map(c => c.id);
                        const allSelected = filteredIds.every(id => newPolitica.controles.includes(id));
                        
                        if (allSelected) {
                          // Desmarcar todos os filtrados
                          setNewPolitica({
                            ...newPolitica,
                            controles: newPolitica.controles.filter(id => !filteredIds.includes(id))
                          });
                        } else {
                          // Marcar todos os filtrados
                          const novosControles = Array.from(new Set([...newPolitica.controles, ...filteredIds]));
                          setNewPolitica({
                            ...newPolitica,
                            controles: novosControles
                          });
                        }
                      }}
                    >
                      {getFilteredControles().length > 0 && 
                       getFilteredControles().every(c => newPolitica.controles.includes(c.id))
                        ? '☑ Desmarcar Todos'
                        : '☐ Selecionar Todos'
                      } ({getFilteredControles().length})
                    </button>
                  </div>

                  {/* Lista de controles */}
                  <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--bs-border-color)', borderRadius: '0.25rem', padding: '0.75rem', backgroundColor: 'var(--bs-body-bg)' }}>
                    {sapControls.length === 0 ? (
                      <p className="text-muted small mb-0">Nenhum controle disponível</p>
                    ) : getFilteredControles().length === 0 ? (
                      <p className="text-muted small mb-0">Nenhum controle encontrado com os filtros aplicados</p>
                    ) : (
                      getFilteredControles().map((controle) => (
                        <div key={controle.id} className="form-check mb-3 p-3" style={{ backgroundColor: newPolitica.controles.includes(controle.id) ? 'rgba(13, 110, 253, 0.1)' : 'transparent', borderRadius: '0.25rem', position: 'relative', border: '1px solid var(--bs-border-color-translucent)' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`edit-controle-${controle.id}`}
                            checked={newPolitica.controles.includes(controle.id)}
                            style={{ position: 'relative', zIndex: 1, marginTop: '0.35rem', flexShrink: 0 }}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewPolitica({
                                  ...newPolitica,
                                  controles: [...newPolitica.controles, controle.id]
                                });
                              } else {
                                setNewPolitica({
                                  ...newPolitica,
                                  controles: newPolitica.controles.filter(id => id !== controle.id)
                                });
                              }
                            }}
                          />
                          <label className="form-check-label w-100" htmlFor={`edit-controle-${controle.id}`} style={{ cursor: 'pointer' }}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div style={{ flex: 1 }}>
                                <strong>{controle.id}</strong> - {controle.name}
                                <br />
                                <small className="text-muted">{controle.description}</small>
                              </div>
                              <div className="d-flex flex-column gap-1 ms-2" style={{ alignItems: 'flex-end' }}>
                                <div className="d-flex gap-1">
                                  <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>{translateCategory(controle.category)}</span>
                                  <span className={`badge ${controle.severity === 'critical' ? 'bg-danger' : controle.severity === 'high' ? 'bg-warning' : controle.severity === 'medium' ? 'bg-info' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                    {translateSeverity(controle.severity)}
                                  </span>
                                </div>
                                {controle.frameworks && controle.frameworks.length > 0 && (
                                  <div className="d-flex gap-1 flex-wrap justify-content-end">
                                    {controle.frameworks.map((fw: string) => (
                                      <span key={fw} className="badge bg-primary" style={{ fontSize: '0.6rem' }}>{fw}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  <small className="text-muted">Selecione os controles que farão parte desta política</small>
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="editAtivoPolitica"
                    checked={newPolitica.ativo}
                    onChange={(e) => setNewPolitica({ ...newPolitica, ativo: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="editAtivoPolitica">
                    Política ativa
                  </label>
                </div>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={closeEditPoliticaModal}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handleEditPolitica}
                  disabled={!newPolitica.nome.trim()}
                >
                  Salvar Alterações
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Confirmar Exclusão de Política */}
        {showDeletePoliticaModal && politicaToDelete && (
          <div className="cowner-modal-overlay" onClick={() => setShowDeletePoliticaModal(false)}>
            <div className="cowner-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="cowner-modal-header">
                <h3>Confirmar Exclusão</h3>
                <button className="cowner-modal-close" onClick={() => setShowDeletePoliticaModal(false)} aria-label="Fechar">
                  ×
                </button>
              </div>
              <div className="cowner-modal-body">
                <div className="alert alert-warning d-flex align-items-start" role="alert">
                  <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor" className="me-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                  </svg>
                  <div>
                    <strong>Atenção!</strong> Esta ação não pode ser desfeita.
                  </div>
                </div>
                <p className="mb-2">
                  Tem certeza que deseja excluir a política:
                </p>
                <p className="mb-0">
                  <strong>"{politicaToDelete.nome}"</strong>
                </p>
              </div>
              <div className="cowner-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeletePoliticaModal(false)}>
                  Cancelar
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={handleDeletePolitica}
                >
                  Excluir Política
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast.visible && (
          <div 
            className="position-fixed bottom-0 end-0 p-3" 
            style={{ zIndex: 11000 }}
          >
            <div 
              className={`alert alert-${toast.variant === 'danger' ? 'danger' : toast.variant === 'success' ? 'success' : 'info'} d-flex align-items-center shadow-lg`}
              role="alert"
              style={{ 
                minWidth: '300px',
                animation: 'slideInRight 0.3s ease-out'
              }}
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 16 16" 
                fill="currentColor" 
                className="me-2 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
              >
                {toast.variant === 'success' ? (
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                ) : toast.variant === 'danger' ? (
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                ) : (
                  <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
                )}
              </svg>
              <div>{toast.message}</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default COwnerCliente;
